import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ErrorBoundary } from '@/components/common/error-boundary';
import { APP_VERSION, APP_BUILD_HASH, APP_BUILD_DATE } from '@/config/constants';
import '@/styles/globals.css';

// ── Production console-log gate ─────────────────────────────────────────
// On deployed hosts (anything not localhost), suppress `console.log` and
// `console.info` by default — they're developer-noise and inflate browser
// memory + obscure user-relevant errors. Power users can re-enable by
// setting `localStorage.viewUADeveloperInfoLogs = "true"` in DevTools.
// `console.warn` and `console.error` are NEVER gated — those carry real
// signal we want surfaced in user reports / Sentry-style tooling.
(() => {
  try {
    const host = window.location.hostname;
    const isLocal =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '0.0.0.0' ||
      host === '[::1]' ||
      host.endsWith('.localhost');
    if (isLocal) return;
    if (localStorage.getItem('viewUADeveloperInfoLogs') === 'true') return;
    const noop = () => { /* suppressed in prod */ };
    console.log = noop;
    console.info = noop;
    console.debug = noop;
  } catch {
    // localStorage / window can throw in exotic environments — leave logs on.
  }
})();

// Log build identity once at boot so it's obvious what's running without
// digging into Settings. APP_VERSION includes a "-dirty" suffix for builds
// made with uncommitted working-tree changes, so no two deploys ever share
// the same version string.
// `console.warn` so it survives the production log gate above — every
// support ticket benefits from knowing what build the user is on.
console.warn(`[Build] v${APP_VERSION} (${APP_BUILD_HASH}) @ ${APP_BUILD_DATE}`);

// Register service worker for PWA + background FCM notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .catch(() => {
        // SW registration failed — non-critical, app works without it
      });
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>
);
